import classNames from "classnames";
import IconButton from "../IconButton";

export type RankedSelectionItem = {
  label: string;
  value: string;
};

export interface RankedSelectionProps {
  items: RankedSelectionItem[];
  onMove: (index: number, direction: -1 | 1) => void;
  onRemove: (value: string) => void;
  disabled?: boolean;
  testIdPrefix: string;
  title?: string;
  className?: string;
}

export default function RankedSelection({
  items,
  onMove,
  onRemove,
  disabled = false,
  testIdPrefix,
  title = "Order of Importance",
  className
}: RankedSelectionProps) {
  if (items.length === 0) {
    return null;
  }

  return (
    <div
      className={classNames("rounded-xl border border-sys-brand-outline-variant bg-sys-brand-surface p-5", className)}
      data-testid={`${testIdPrefix}-ranking`}
    >
      <h4 className="mb-4 text-title-medium-bold">{title}</h4>
      <div className="flex flex-col gap-3">
        {items.map((item, index) => (
          <div
            key={item.value}
            className="flex w-full min-w-0 flex-col lg:flex-row lg:flex-nowrap lg:items-center lg:justify-between"
            data-testid={`${testIdPrefix}-rank-row-${item.value}`}
          >
            <div className="flex w-full min-w-0 flex-1 items-center space-x-2">
              <span
                className="mr-2 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[6px] bg-extended-pgasilver-brand-color text-body-large-semibold text-extended-pgasilver-brand-on-color"
                data-testid={`${testIdPrefix}-rank-badge-${item.value}`}
              >
                {index + 1}
              </span>
              <span className="min-w-0 flex-1 break-words text-body-large-semibold text-sys-brand-on-surface">
                {item.label}
              </span>
            </div>
            <div
              className="mt-1 flex items-center justify-end lg:ml-2 lg:mt-0 lg:shrink-0 lg:self-center"
              data-testid={`${testIdPrefix}-rank-actions-${item.value}`}
            >
              <IconButton
                name="arrow_upward"
                skipMargin
                ariaLabel={`Move ${item.label} up`}
                data-testid={`${testIdPrefix}-move-up-${item.value}`}
                disabled={disabled || index === 0}
                type="button"
                onClick={() => onMove(index, -1)}
              />
              <IconButton
                name="arrow_downward"
                skipMargin
                ariaLabel={`Move ${item.label} down`}
                data-testid={`${testIdPrefix}-move-down-${item.value}`}
                disabled={disabled || index === items.length - 1}
                type="button"
                onClick={() => onMove(index, 1)}
              />
              <IconButton
                name="close"
                skipMargin
                ariaLabel={`Remove ${item.label}`}
                data-testid={`${testIdPrefix}-remove-${item.value}`}
                disabled={disabled}
                type="button"
                onClick={() => onRemove(item.value)}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
